import { readonly, ref } from 'vue'

// App-wide toasts: confirmations shown at the top of the viewport, wherever the
// visitor has scrolled. One <AppToaster> in App.vue renders them. Anything that
// belongs to a field or a control (validation, a failed upload) stays inline
// next to it instead.

type ToastTone = 'success' | 'error'
interface Toast {
  id: number
  message: string
  tone: ToastTone
}

export const TOAST_DURATION_MS = 4000
const MAX_TOASTS = 3

const toasts = ref<Toast[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextId = 1

function stopTimer(id: number) {
  clearTimeout(timers.get(id))
  timers.delete(id)
}
function dismiss(id: number) {
  stopTimer(id)
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}
// Only success toasts time out; an error waits until it is closed.
function startTimer(toast: Toast) {
  stopTimer(toast.id)
  if (toast.tone !== 'success') return
  const timer = setTimeout(() => {
    dismiss(toast.id)
  }, TOAST_DURATION_MS)
  timers.set(toast.id, timer)
}
function show(message: string, tone: ToastTone): number {
  // A repeat replaces the one showing, as a new node so screen readers announce it again.
  for (const toast of toasts.value)
    if (toast.message === message && toast.tone === tone) dismiss(toast.id)
  const toast: Toast = { id: nextId++, message, tone }
  toasts.value = [...toasts.value, toast]
  for (const oldest of toasts.value.slice(0, -MAX_TOASTS)) dismiss(oldest.id)
  startTimer(toast)
  return toast.id
}
// Hovering or focusing a toast holds it open; leaving restarts its full time.
function pause(id: number) {
  stopTimer(id)
}
function resume(id: number) {
  const toast = toasts.value.find((candidate) => candidate.id === id)
  if (toast) startTimer(toast)
}
function clear() {
  for (const toast of toasts.value) stopTimer(toast.id)
  toasts.value = []
}

export function useToasts() {
  return {
    toasts: readonly(toasts),
    success: (message: string) => show(message, 'success'),
    error: (message: string) => show(message, 'error'),
    dismiss,
    pause,
    resume,
    clear,
  }
}
